import { Badge } from "@/components/shadcn/badge";
import { Card, CardContent } from "@/components/shadcn/card";
import { CalendarDays, Users, Video } from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import Image from "next/image";
import { RequirementBadge } from "../requirement-badge";
import EventLinkButton from "./event-link-button";
import { formatInstructorName } from "./format-instructor-name";
import { WebinarEventWithUserStatus } from "./types";
import { WebinarJoinButton } from "./webinar-join-button";

export function WebinarUpcomingCard({
    event,
}: {
    event: WebinarEventWithUserStatus;
}) {
    const t = useTranslations("events");
    const format = useFormatter();

    const eventDate = new Date(event.date);
    const dateStr = format.dateTime(eventDate, "long");
    const timeStr = format.dateTime(eventDate, "time");
    const instructorName = formatInstructorName(event.instructor);

    return (
        <Card className="h-full">
            <CardContent className="flex h-full flex-col gap-4 sm:flex-row sm:gap-5">
                <div className="relative aspect-video shrink-0 overflow-hidden rounded-lg sm:aspect-auto sm:w-2/5">
                    {event.imageUrl ? (
                        <Image
                            src={event.imageUrl}
                            alt={event.title}
                            fill
                            className="object-cover"
                            sizes="(max-width: 640px) 100vw, 25vw"
                        />
                    ) : (
                        <div className="bg-muted flex size-full items-center justify-center">
                            <Video className="text-muted-foreground size-12" />
                        </div>
                    )}
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge>
                            <Users />
                            {event.participantCount}/{event.capacity}
                        </Badge>
                        <RequirementBadge isPremium={event.isPremium} />
                    </div>

                    <div className="flex flex-1 flex-col justify-center gap-2">
                        <Badge className="bg-primary/10 text-primary">
                            <CalendarDays />
                            {dateStr} · {timeStr}
                        </Badge>
                        <h3 className="text-sm font-medium">{event.title}</h3>
                        <p className="text-muted-foreground text-xs">
                            {t("trainer")}: {instructorName}
                        </p>
                    </div>

                    <div className="flex flex-col gap-1.5 sm:max-w-48">
                        <WebinarJoinButton event={event} />
                        <EventLinkButton event={event} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
